import React, { useEffect, useRef, useState } from "react";
import { KeyboardDatePicker } from "@material-ui/pickers";
import moment from "moment";
import { makeStyles } from "@material-ui/core/styles";
import EventIcon from "@material-ui/icons/Event";
import * as customColors from "../../theme/customColors";

const useStyles = makeStyles((theme) => ({
  datePicker: {
    "& .MuiInputBase-root": {
      borderRadius: "20px"
    },
    "& .MuiOutlinedInput-input": {
      boxSizing: "border-box",
      fontSize: "13px",
      minWidth: 0,
      paddingLeft: "8px",
      paddingRight: "4px"
    },
    "& .MuiOutlinedInput-input::placeholder": {
      fontSize: "13px",
      opacity: 1
    },
    "& .MuiInputAdornment-root": {
      marginLeft: 0,
      marginRight: 0
    },
    "& .MuiIconButton-root": {
      padding: "4px"
    }
  }
}));

const DATE_FIELD_MIN_WIDTH = 148;

const DatePickerField = function (props) {
  const classes = useStyles();
  const lastPropValueRef = useRef(props.value);

  // Convert YYYY-MM-DD string to moment object (or null)
  const valueToMoment = (value) => {
    if (!value || value === "" || value === undefined || value === null) return null;
    try {
      if (moment.isMoment(value)) return value.isValid() ? value : null;
      if (value instanceof Date) return moment(value);

      const strictYmd = moment(value, "YYYY-MM-DD", true);
      if (strictYmd.isValid()) return strictYmd;

      const strictDmy = moment(value, "DD/MM/YYYY", true);
      if (strictDmy.isValid()) return strictDmy;

      const fallback = moment(value);
      return fallback.isValid() ? fallback : null;
    } catch (error) {
      console.error("Error parsing date value:", value, error);
      return null;
    }
  };

  // Convert moment object to YYYY-MM-DD string (or empty string)
  const momentToValue = (momentObj) => {
    if (!momentObj || !momentObj.isValid()) return "";
    return momentObj.format("YYYY-MM-DD");
  };

  const formatDisplayValue = (value) => {
    const parsed = valueToMoment(value);
    return parsed ? parsed.format("DD/MM/YYYY") : "";
  };

  const [inputValue, setInputValue] = useState(formatDisplayValue(props.value));

  useEffect(() => {
    if (props.value !== lastPropValueRef.current) {
      lastPropValueRef.current = props.value;
      setInputValue(formatDisplayValue(props.value));
    }
  }, [props.value]);

  const handleChange = (date, keyboardInputValue) => {
    if (props.onChange) {
      try {
        const hasValidDate = date && moment(date).isValid();
        const isCleared = keyboardInputValue === "" || keyboardInputValue === null || keyboardInputValue === undefined;

        if (keyboardInputValue !== undefined && keyboardInputValue !== null) {
          setInputValue(keyboardInputValue);
        } else if (hasValidDate) {
          setInputValue(moment(date).format("DD/MM/YYYY"));
        }

        // Only update external state when we have a valid date or the user cleared the input.
        // This preserves partially typed values in the picker UI.
        if (hasValidDate || isCleared) {
          const value = hasValidDate ? momentToValue(moment(date)) : "";
          // Create synthetic event to match expected format
          const syntheticEvent = {
            target: {
              name: props.name,
              value: value
            }
          };
          props.onChange(syntheticEvent);
        }
      } catch (error) {
        console.error("Error in DatePickerField handleChange:", error);
      }
    }
  };

  const handleBlur = () => {
    if (props.onBlur) {
      const syntheticEvent = {
        target: {
          name: props.name,
          value: props.value || ""
        }
      };
      props.onBlur(syntheticEvent);
    }
  };

  // Set background color (matching LabeledTextField logic)
  let background = customColors.white;
  if (props.background) background = customColors.orange;
  else if (props.disabled) background = customColors.veryLigntGray;

  const requestedWidth = props.textFieldWidth || 190;
  const textFieldWidth = Math.max(requestedWidth, DATE_FIELD_MIN_WIDTH) + "px";

  // Convert minDate/maxDate from YYYY-MM-DD strings to moment objects
  const minDate = props.minDate ? moment(props.minDate, "YYYY-MM-DD", true) : null;
  const maxDate = props.maxDate ? moment(props.maxDate, "YYYY-MM-DD", true) : null;

  const momentValue = valueToMoment(props.value);
  const combinedClassName = [classes.datePicker, props.className].filter(Boolean).join(" ");

  // Build picker props conditionally
  const pickerProps = {
    className: combinedClassName,
    disabled: props.disabled,
    size: "small",
    format: "DD/MM/YYYY",
    value: momentValue,
    inputValue: inputValue,
    onChange: handleChange,
    onBlur: handleBlur,
    error: props.error,
    helperText: props.helperText,
    invalidDateMessage: "",
    inputVariant: "outlined",
    fullWidth: true,
    inputProps: {
      name: props.name,
      placeholder: props.placeholder || "DD/MM/YYYY",
      dir: "ltr"
    },
    InputProps: {
      style: {
        WebkitBoxShadow: "0 0 0 1000px " + background + " inset",
        background: background,
        borderRadius: "20px"
      }
    },
    InputLabelProps: {
      shrink: false
    },
    InputAdornmentProps: {
      position: "start"
    },
    keyboardIcon: <EventIcon />,
    clearable: false,
    autoOk: true
  };

  // Only add minDate/maxDate if they're valid
  if (minDate && minDate.isValid()) {
    pickerProps.minDate = minDate;
  }
  if (maxDate && maxDate.isValid()) {
    pickerProps.maxDate = maxDate;
  }

  return (
    <div style={{ width: textFieldWidth }}>
      <KeyboardDatePicker {...pickerProps} />
    </div>
  );
};

export default DatePickerField;
